import { Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { GuestPurchaserUser } from '../../../interfaces/guest-purchaser-response.interface';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDeleteDialogComponent } from '../confirm-delete-dialog/confirm-delete-dialog.component';
import { buildInviteFailureState } from '../../../Shared Components/Utilities/utils';

@Component({
  selector: 'app-user-perms',
  templateUrl: './user-perms.component.html',
  styleUrl: './user-perms.component.scss',
  standalone: false,
})
export class UserPermsComponent {
  inviteEmailForm!: FormGroup;
  emails: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA];
  users: GuestPurchaserUser[] = [];
  totalRecords = 0;
  isLoading = false;
  displayedColumns: string[] = ['userName', 'email', 'Role', 'actions'];
  dataSource = new MatTableDataSource<GuestPurchaserUser>();
  inviteErrorMessage: string = '';
  failedEmails: string[] = [];
  failedEmailMessages: string[] = [];
  readonly inviteRoleCode = 'GPRC';

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly backend: BackendAccessService,
    private readonly loading: LoadingService,
    private readonly dialog: MatDialog
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    this.inviteEmailForm = this.formBuilder.group({
      emailInput: ['', [Validators.email]],
    });
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    this.loadUsers();
  }

  /* istanbul ignore next */
  loadUsers(): void {
    // Use paginator values if available, otherwise default to 0 and 10
    const pageIndex = this.paginator ? this.paginator.pageIndex : 0;
    const pageSize = this.paginator ? this.paginator.pageSize : 6;

    this.loading.show();
    this.backend.getGuestPurchaserUsersZentra(pageIndex, pageSize).subscribe({
      next: (response) => {
        this.dataSource.data = response.userList;
        this.totalRecords = response.totalRecords; // Store the total from API
        this.loading.hide();
      },
      error: (err) => {
        console.error('Error loading users:', err);
        this.loading.hide();
      },
    });
  }

  /* istanbul ignore next */
  onPageChange(event: any): void {
    this.loadUsers();
  }

  /* istanbul ignore next */
  deleteUser(user: GuestPurchaserUser) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      data: { name: user.userName },
      panelClass: 'custom-dialog-container', // Optional: for global style overrides
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.executeDelete(user);
      }
    });
  }
  /* istanbul ignore next */
  executeDelete(user: GuestPurchaserUser) {
    // Show the global loading spinner
    this.loading.show();

    this.backend.deleteGuestPurchaser(user.permissionId).subscribe({
      next: (response) => {
        console.log('User deleted successfully');

        // Refresh the list so the card disappears from the UI
        this.loadUsers();

        // The loading.hide() is usually handled inside loadUsers()
        // when the new list finishes fetching.
      },
      error: (error) => {
        console.error('Error deleting user:', error);
        this.loading.hide();
        // Optional: Add a toast message or alert here if the delete fails
      },
    });
  }

  /* istanbul ignore next */
  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value && this.isValidEmail(value) && !this.emails.includes(value)) {
      this.emails.push(value);
      this.inviteErrorMessage = ''; // Clear error when adding new email
      this.failedEmails = [];
      this.failedEmailMessages = [];
    }

    event.chipInput?.clear();
    this.inviteEmailForm.get('emailInput')?.setValue('');
  }

  /* istanbul ignore next */
  private isValidEmail(email: string): boolean {
    // Use Angular's built-in email validator logic
    const control = this.formBuilder.control(email, [Validators.email]);
    return control.valid;
  }
  /* istanbul ignore next */
  remove(email: string): void {
    const index = this.emails.indexOf(email);
    if (index >= 0) {
      this.emails.splice(index, 1);
      this.inviteErrorMessage = ''; // Clear error when removing email
      this.failedEmails = [];
      this.failedEmailMessages = [];
    }
  }
  /* istanbul ignore next */
  getAvatarColor(name: string): string {
    const colors = [
      '#FF5722', // Deep Orange
      '#673AB7', // Deep Purple
      '#009688', // Teal
      '#3F51B5', // Indigo
      '#E91E63', // Pink
      '#FFB300', // Amber
      '#4CAF50', // Green
    ];

    if (!name) return colors[0];

    // Simple hash to pick a color based on name string
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
  }
  /* istanbul ignore next */
  getInitials(name: string | undefined | null): string {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  }
  /* istanbul ignore next */
  applyFilter(event: Event | { target: HTMLInputElement }) {
    const filterValue = (event.target as HTMLInputElement).value;

    // This triggers the built-in search logic in MatTableDataSource
    this.dataSource.filter = filterValue.trim().toLowerCase();

    // If you have a paginator, go back to the first page when searching
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /* istanbul ignore next */
  onSubmit(): void {
    this.loading.show();
    this.inviteErrorMessage = ''; // Clear any previous errors
    this.failedEmails = [];
    this.failedEmailMessages = [];
    if (this.emails.length > 0) {
      const payload = {
        users: this.emails.map((email) => ({
          emailAddress: email,
          roleCode: this.inviteRoleCode,
        })),
      };
      this.backend.sendInvite(payload).subscribe({
        next: (response: any) => {
          // Check if the response indicates a failure
          if (response?.isSuccess === false && response?.errors) {
            // Parse failed emails and use the API's own error message for each
            const failureState = buildInviteFailureState(response.errors);
            this.failedEmails = failureState.failedEmails;
            this.failedEmailMessages = failureState.failedEmailMessages;
            this.inviteErrorMessage = failureState.inviteErrorMessage;

            // Keep only the failed emails in the input, remove successful ones
            this.emails = this.failedEmails;

            // Refresh the user list to show any successfully added users
            this.loadUsers();
          } else {
            console.log('Invite successful:', response);
            this.emails = [];
            this.loadUsers();
          }
        },
        error: (error) => {
          console.error('Error inviting users:', error);
          this.inviteErrorMessage =
            error?.error?.message ||
            'An error occurred while sending invitations. Please try again.';
          this.loading.hide();
        },
      });
    }
  }
}
